import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Grid,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../../app/utils/formatters';
import { PaymentMethod } from '../../../../app/data/models/payments/paymentMethod';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentMethod: PaymentMethod;
  amountReceived: string;
  referenceNumber: string;
  printReceipt: boolean;
  processingAction: boolean;
  remainingAmount: number;
  onPaymentMethodChange: (event: SelectChangeEvent<PaymentMethod>) => void;
  onAmountReceivedChange: (value: string) => void;
  onReferenceNumberChange: (value: string) => void;
  onPrintReceiptChange: (checked: boolean) => void;
  onPaymentSubmit: () => void;
  calculateChange: () => number;
  getPaymentMethodDisplay: (method: PaymentMethod) => string;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  paymentMethod,
  amountReceived,
  referenceNumber,
  printReceipt,
  processingAction,
  remainingAmount,
  onPaymentMethodChange,
  onAmountReceivedChange,
  onReferenceNumberChange,
  onPrintReceiptChange,
  onPaymentSubmit,
  calculateChange,
  getPaymentMethodDisplay,
}) => {
  const { t } = useTranslation();
  const showReferenceField = paymentMethod !== PaymentMethod.Cash;
  const showChangeAmount =
    paymentMethod === PaymentMethod.Cash && Number(amountReceived) > 0;
  const changeAmount = calculateChange();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('biller.add-payment')}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('biller.amount-to-pay')}: {formatCurrency(remainingAmount)}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="payment-method-label">
                  {t('biller.payment-method')}
                </InputLabel>
                <Select
                  labelId="payment-method-label"
                  value={paymentMethod}
                  onChange={onPaymentMethodChange}
                  label={t('biller.payment-method')}
                >
                  {Object.values(PaymentMethod)
                    .filter((value) => typeof value === 'number')
                    .map((method) => (
                      <MenuItem key={method} value={method as PaymentMethod}>
                        {getPaymentMethodDisplay(method as PaymentMethod)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('biller.amount-received')}
                type="number"
                fullWidth
                value={amountReceived}
                onChange={(e) => onAmountReceivedChange(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ mb: 2 }}
              />
            </Grid>
            {showReferenceField && (
              <Grid item xs={12}>
                <TextField
                  label={t('biller.reference-number')}
                  fullWidth
                  value={referenceNumber}
                  onChange={(e) => onReferenceNumberChange(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
            )}
            {showChangeAmount && (
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('biller.change')}: {formatCurrency(changeAmount)}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printReceipt}
                    onChange={(e) => onPrintReceiptChange(e.target.checked)}
                  />
                }
                label={t('biller.print-receipt')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processingAction}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onPaymentSubmit}
          color="primary"
          variant="contained"
          disabled={
            processingAction ||
            !amountReceived ||
            Number(amountReceived) <= 0 ||
            (showReferenceField && !referenceNumber)
          }
        >
          {processingAction ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('common.submit')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
